import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Library, Newspaper, Heart, Palette, Images, Info, Settings, LogOut,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTapGuard } from '../utils/tap';

const CLOSE_MS = 200;

// The app's menu: a panel that slides in from the left, its entries arriving one after
// the other. Same entries and callbacks as always (the name stays from when it was a
// dropdown under the button).
export function MenuDropdown({ onNewsPress, onCollectionPress, onUpdatesPress, onInfoPress, onSignOutPress, onCommissionsPress, onCommissionsGalleryPress, onSettingsPress, isDarkMode, isMasterUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  const { t } = useLanguage();
  const guard = useTapGuard();

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // the page behind does not scroll while the menu is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  const close = () => {
    if (!isOpen || closing) return;
    setClosing(true);
    closeTimer.current = setTimeout(() => { setIsOpen(false); setClosing(false); }, CLOSE_MS);
  };

  const toggle = () => {
    if (isOpen) close();
    else setIsOpen(true);
  };

  const handleMenuClick = (callback) => {
    clearTimeout(closeTimer.current);
    setIsOpen(false);
    setClosing(false);
    callback();
  };

  // Master already has every comic and no per-account preferences, so it skips those two.
  const items = [
    !isMasterUser && { key: 'collection', label: t('myCollection'), onPress: onCollectionPress, Icon: Library, color: '#ffd400' },
    { key: 'news', label: t('news'), onPress: onUpdatesPress, Icon: Newspaper, color: '#22e0ff' },
    { key: 'patreon', label: t('patreon'), onPress: onNewsPress, Icon: Heart, color: '#ff7ac0' },
    { key: 'commissions', label: t('commissions'), onPress: onCommissionsPress, Icon: Palette, color: '#ffb347' },
    { key: 'gallery', label: t('commissionsCompleted'), onPress: onCommissionsGalleryPress, Icon: Images, color: '#ff8a5c' },
    { key: 'info', label: t('infoCredits'), onPress: onInfoPress, Icon: Info, color: '#ffffff' },
    !isMasterUser && { key: 'settings', label: t('settings'), onPress: onSettingsPress, Icon: Settings, color: '#d9c8f5' },
  ].filter(Boolean);

  return (
    <div style={styles.container}>
      <button
        onClick={guard(toggle)}
        className={`fp-burger${isOpen && !closing ? ' fp-burger--open' : ''}`}
        style={isDarkMode ? styles.burgerDark : undefined}
        aria-label="Menu"
        data-testid="menu-button"
      >
        <span /><span /><span />
      </button>

      {isOpen && createPortal(
        <>
          <div className={`fp-drawer-backdrop${closing ? ' fp-drawer-backdrop--closing' : ''}`} onClick={guard(close)} />
          <nav className={`fp-drawer fp-noscrollbar${isDarkMode ? ' fp-drawer--dark' : ''}${closing ? ' fp-drawer--closing' : ''}`} data-testid="menu-drawer">
            <div style={styles.drawerHead}>
              <img src="/logo.png" alt="FootParadise" style={styles.drawerLogo} />
              <button className="fp-burger fp-burger--open" style={styles.closeButton} onClick={guard(close)} aria-label="Close">
                <span /><span /><span />
              </button>
            </div>

            {items.map(({ key, label, onPress, Icon, color }, i) => (
              <button
                key={key}
                className="fp-drawer-item"
                style={{ '--i': i }}
                onClick={guard(() => handleMenuClick(onPress), 'item')}
                data-testid={`menu-item-${key}`}
              >
                <span className="fp-drawer-icon" style={{ background: color }}>
                  <Icon size={18} color="#14061f" strokeWidth={2.6} />
                </span>
                {label}
              </button>
            ))}

            <div style={styles.spacer} />
            <button
              className="fp-drawer-item"
              style={{ '--i': items.length, ...styles.signOut }}
              onClick={guard(() => handleMenuClick(onSignOutPress), 'item')}
              data-testid="menu-item-signout"
            >
              <span className="fp-drawer-icon" style={{ background: '#ff5c7a' }}>
                <LogOut size={18} color="#14061f" strokeWidth={2.6} />
              </span>
              {t('signOut')}
            </button>
          </nav>
        </>,
        document.body
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
  },
  burgerDark: {
    background: 'linear-gradient(180deg, #5a5a5a 0%, #2a2a2a 100%)',
  },
  drawerHead: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px',
  },
  drawerLogo: {
    height: '26px',
    maxWidth: '65%',
    objectFit: 'contain',
  },
  closeButton: {
    width: '40px',
    height: '40px',
    transform: 'scale(0.9)',
  },
  spacer: {
    flex: 1,
    minHeight: '6px',
  },
  signOut: {
    marginBottom: 0,
    color: '#ffb3c2',
  },
};
